import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { Button } from "~/foundation/components/Button";

// Thinker fabric §1.2 / §7 — who this project gives work to.
//
// Owner, 2026-10-04: "dev should be a role that is dynamic and synthetic — not always there will be a dev …
// roles … linked to a workspace-collection-project-team-area of project — assigned to an architectural branch …
// the models of the branches know their branch, not the whole project."
//
// The panel is deliberately a list of SENTENCES rather than a form of ids: a role reads as "shader-artist runs on
// <thinker>, inside <branch>, for <project>/<area>". Four roles ship as seeds; anything a project declares joins
// them, and the narrower binding wins.
//
// It writes the project's preference — it does not ask a model anything, and nothing here changes how the agent
// is permitted to act. The one thing it binds is which thinker a named role runs on.

interface RoleRow {
  name: string;
  kind: string;
  thinkerId?: string | null;
  scope?: string | null;
  project?: string | null;
  area?: string | null;
  description?: string | null;
  origin: string;
}

interface ModelRow {
  provider: string;
  model: string;
  maxContextTokens: number | null;
  maxOutputTokens: number | null;
  effort: { parameter: string; levels: string[]; default: string | null } | null;
  inputUsdPerMillion: number | null;
  outputUsdPerMillion: number | null;
  notes: string | null;
}

/** A role in one line, the way a person says it. */
export function roleSentence(role: RoleRow, t: TFunction): string {
  const where = [
    role.scope ? t("inside {{scope}}", { scope: role.scope }) : null,
    role.project ? t("for {{project}}", { project: role.project }) : null,
    role.area ? t("in {{area}}", { area: role.area }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const who =
    role.kind === "human" ? t("a person") : (role.thinkerId ?? t("whatever the project map says"));
  return where ? `${who} — ${where}` : who;
}

export function Roles() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  // The WORK projects (they carry the key prefix roles are narrowed by), not the governance list.
  const projects = useQuery({
    queryKey: ["work", "projects", "forRoles"],
    queryFn: async () =>
      (
        (await api.work.projects()) as {
          data?: Array<{ id: string; keyPrefix: string; name: string }>;
        }
      )?.data ?? [],
    retry: false,
  });
  const [projectKey, setProjectKey] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<RoleRow>({ name: "", kind: "ai", origin: "declared" });
  const [failed, setFailed] = useState<string | null>(null);

  const key = projectKey ?? projects.data?.[0]?.keyPrefix ?? null;

  const roles = useQuery({
    queryKey: ["work", "roles", key],
    queryFn: async () =>
      (await api.work.call("work.roles", key ? { projectKey: key } : {})) as {
        roles: RoleRow[];
        models: ModelRow[];
        project: { key: string; name: string };
      },
    enabled: Boolean(key),
    retry: false,
  });

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.work.call("work.roles", { projectKey: key, ...body }),
    onSuccess: () => {
      setFailed(null);
      setAdding(false);
      setDraft({ name: "", kind: "ai", origin: "declared" });
      void queryClient.invalidateQueries({ queryKey: ["work", "roles", key] });
    },
    onError: (error: unknown) => setFailed(error instanceof Error ? error.message : String(error)),
  });

  const data = roles.data;
  const models = data?.models ?? [];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-text-secondary">
        {t(
          "Who this project gives work to. A role is any name you like — the four below are only defaults. Bind a role to a branch and the thinker running it works inside that branch rather than the whole project.",
        )}
      </p>

      {(projects.data ?? []).length > 1 ? (
        <select
          aria-label={t("Project")}
          value={key ?? ""}
          onChange={(e) => setProjectKey(e.target.value || null)}
          className="w-fit rounded-md border border-input-border bg-bg p-2 text-sm text-text"
        >
          {(projects.data ?? []).map((p) => (
            <option key={p.id} value={p.keyPrefix}>
              {p.keyPrefix} · {p.name}
            </option>
          ))}
        </select>
      ) : null}

      {roles.isLoading ? <p className="text-sm text-text-secondary">{t("Loading…")}</p> : null}

      <ul className="flex flex-col gap-2" data-testid="roles-list">
        {(data?.roles ?? []).map((role) => (
          <li
            key={`${role.name}@${role.project ?? ""}/${role.area ?? ""}/${role.scope ?? ""}`}
            className="flex flex-wrap items-center gap-2 rounded-md border border-divider p-3"
          >
            <span className="font-medium text-sm text-text">{role.name}</span>
            <span className="text-sm text-text-secondary">{roleSentence(role, t)}</span>
            {role.origin === "seeded" ? (
              <span className="rounded-md bg-bg-subtle px-2 py-0.5 text-text-tertiary text-xs">
                {t("default")}
              </span>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="ms-auto"
                onClick={() => save.mutate({ remove: [role.name] })}
                disabled={save.isPending}
              >
                {t("Put back to the default")}
              </Button>
            )}
          </li>
        ))}
      </ul>

      {adding ? (
        <div
          className="flex flex-col gap-2 rounded-md border border-divider p-3"
          data-testid="roles-add"
        >
          <input
            aria-label={t("Role name")}
            placeholder={t("shader-artist")}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            className="rounded-md border border-input-border bg-bg p-2 text-sm text-text"
          />
          <input
            aria-label={t("Which thinker runs it")}
            placeholder={t("A thinker id, or leave empty to use the project map")}
            value={draft.thinkerId ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, thinkerId: e.target.value || null }))}
            className="rounded-md border border-input-border bg-bg p-2 text-sm text-text"
          />
          <input
            aria-label={t("Which branch it works inside")}
            placeholder={t("A scope slug — the architectural branch (optional)")}
            value={draft.scope ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, scope: e.target.value || null }))}
            className="rounded-md border border-input-border bg-bg p-2 text-sm text-text"
          />
          <input
            aria-label={t("Area")}
            placeholder={t("One area of the project (optional)")}
            value={draft.area ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, area: e.target.value || null }))}
            className="rounded-md border border-input-border bg-bg p-2 text-sm text-text"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!draft.name.trim() || save.isPending}
              onClick={() => save.mutate({ roles: [{ ...draft, project: key }] })}
            >
              {t("Add the role")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              {t("Cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="ghost" size="sm" className="w-fit" onClick={() => setAdding(true)}>
          {t("Add a role")}
        </Button>
      )}

      {failed ? (
        <p className="text-danger text-sm" data-testid="roles-failed">
          {failed}
        </p>
      ) : null}

      <div className="mt-2 flex flex-col gap-1">
        <h3 className="font-medium text-sm text-text">{t("Models this build can measure")}</h3>
        <p className="text-text-tertiary text-xs">
          {t(
            "A model that is not listed still runs — but nothing can be fitted to it, because its context window is unknown here.",
          )}
        </p>
        <ul className="mt-1 flex flex-col gap-0.5" data-testid="roles-models">
          {models.map((m) => (
            <li key={m.model} className="text-text-secondary text-xs">
              <span className="font-mono">{m.model}</span>
              {m.maxContextTokens
                ? ` · ${t("{{n}}K context", { n: Math.round(m.maxContextTokens / 1000) })}`
                : ""}
              {m.effort
                ? ` · ${t("effort {{levels}}", { levels: m.effort.levels.join("/") })}`
                : ` · ${t("no effort setting")}`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
